'use client'

import { m } from 'framer-motion'
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Boxes,
  Calendar,
  CircleCheck,
  Cloud,
  Code,
  Cpu,
  CreditCard,
  Database,
  FileText,
  Gauge,
  Globe,
  Headphones,
  Image as ImageIcon,
  Languages,
  LayoutDashboard,
  Lightbulb,
  Lock,
  type LucideIcon,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Monitor,
  Palette,
  Plug,
  Puzzle,
  Rocket,
  Search,
  Server,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Store,
  Users,
  Video,
  Workflow,
} from 'lucide-react'
import Link from 'next/link'
import type { ReactNode } from 'react'

import { type FeatureIcon, normalizeFeatureIcon } from '@/lib/cases/featureIcons'

type InfoLink = {
  label?: string
  value?: string
  href?: string
}

type InfoText = {
  label?: string
  value?: string
}

type InfoBudget = {
  label?: string
  value?: string
  note?: string
}

type InfoFeature = {
  label: string
  icon?: FeatureIcon
}

export type CaseInfoBlock = {
  status?: { label?: string; value?: string }
  source?: InfoLink
  core?: InfoText
  dataType?: InfoText
  objective?: string | string[] | { label?: string; lines?: string[] }
  budget?: InfoBudget
  features?: InfoFeature[] | string[]
  footer?: { left?: string; right?: string }
}

function asLines(value: CaseInfoBlock['objective']): { label?: string; lines: string[] } {
  if (!value) return { lines: [] }
  if (typeof value === 'string') return { lines: [value] }
  if (Array.isArray(value)) return { lines: value.filter(Boolean).map(String) }
  return { label: value.label, lines: (value.lines || []).filter(Boolean).map(String) }
}

const FEATURE_ICON_COMPONENTS: Record<FeatureIcon, LucideIcon> = {
  activity: Activity,
  database: Database,
  shield: ShieldCheck,
  cpu: Cpu,
  code: Code,
  server: Server,
  cloud: Cloud,
  lock: Lock,
  smartphone: Smartphone,
  monitor: Monitor,
  layout: LayoutDashboard,
  palette: Palette,
  search: Search,
  chart: BarChart3,
  gauge: Gauge,
  plug: Plug,
  workflow: Workflow,
  puzzle: Puzzle,
  settings: Settings,
  bot: Bot,
  mail: Mail,
  message: MessageSquare,
  bell: Bell,
  calendar: Calendar,
  map: MapPin,
  card: CreditCard,
  cart: ShoppingCart,
  store: Store,
  boxes: Boxes,
  file: FileText,
  image: ImageIcon,
  video: Video,
  language: Languages,
  users: Users,
  headphones: Headphones,
  megaphone: Megaphone,
  lightbulb: Lightbulb,
  rocket: Rocket,
  check: CircleCheck,
}

function iconForFeature(feature: InfoFeature): LucideIcon {
  return FEATURE_ICON_COMPONENTS[normalizeFeatureIcon(feature.icon)]
}

// One fact about the project in its own box. Boxes in a row take the height of the
// tallest, so a long client name or project type makes its neighbours taller rather than
// pushing the rest of the block down, and long text wraps inside its box.
function InfoTile({
  icon,
  label,
  preserveLines = false,
  children,
}: {
  icon: ReactNode
  label: string
  preserveLines?: boolean
  children: ReactNode
}) {
  return (
    <div className="flex h-full min-w-0 flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
      <span className="home-meta mb-3 flex items-center gap-2 text-blue-300 transition-colors group-hover:text-blue-200">
        {icon} {label}
      </span>
      <div
        className={[
          'home-card-title-sm tracking-wide [text-wrap:balance] break-words text-white',
          preserveLines ? 'whitespace-pre-line' : '',
        ].join(' ')}
      >
        {children}
      </div>
    </div>
  )
}

export default function CaseInfo({ info }: { info?: CaseInfoBlock | null }) {
  if (!info) return null

  const statusLabel = info.status?.label ?? 'Статус системы'
  const statusValue = info.status?.value ?? 'ONLINE'

  const sourceLabel = info.source?.label ?? 'Источник'
  const sourceValue = info.source?.value ?? ''
  const sourceHref = info.source?.href

  const coreLabel = info.core?.label ?? 'Ядро'
  const coreValue = info.core?.value ?? ''

  const typeLabel = info.dataType?.label ?? 'Тип данных'
  const typeValue = info.dataType?.value ?? ''
  const typeValueParts = typeValue
    .split(/[+＋]/)
    .map((part) => part.trim())
    .filter(Boolean)
  const typeValueFormatted =
    typeValueParts.length > 1 ? typeValueParts.join('\n+ ') : typeValue.trim()

  const objective = asLines(info.objective)
  const objectiveLabel = objective.label ?? 'Что сделали'

  const budgetLabel = info.budget?.label ?? 'Бюджет'
  const budgetValue = info.budget?.value ?? ''
  const budgetNote = info.budget?.note ?? 'RUB'
  const budgetCompact = budgetValue.replace(/\s+/g, '').length > 11

  const features: InfoFeature[] = Array.isArray(info.features)
    ? (info.features as unknown[])
        .map((f): InfoFeature | null => {
          if (typeof f === 'string') {
            return { label: f, icon: 'activity' as const }
          }
          if (!f || typeof f !== 'object') return null

          const item = f as Record<string, unknown>
          const label = typeof item.label === 'string' ? item.label.trim() : ''
          if (!label) return null

          const icon = normalizeFeatureIcon(item.icon)
          return { label, icon }
        })
        .filter((feature): feature is InfoFeature => Boolean(feature))
    : []

  const footerLeft = info.footer?.left ?? 'Цифровой горизонт'
  const footerRight = info.footer?.right ?? 'ver 2.0'

  return (
    <section className="home-section relative z-20 bg-transparent">
      <div className="home-container">
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.03] p-1 backdrop-blur-md sm:rounded-[40px]">
          <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.14)_0%,transparent_55%)]" />

          <div className="grid grid-cols-1 gap-1 overflow-hidden rounded-[24px] bg-black/20 sm:rounded-[36px] lg:grid-cols-12">
            <div className="group relative min-w-0 bg-[#0c1220]/60 p-6 sm:p-8 md:p-12 lg:col-span-7 xl:col-span-8">
              <div className="absolute top-0 left-0 h-[1px] w-full bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-70" />

              <div className="mb-8 flex items-center gap-4 sm:mb-10">
                <div className="h-3 w-3 animate-pulse rounded-full bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.8)]" />
                <span className="home-meta text-blue-300/80">
                  {statusLabel}: {statusValue}
                </span>
              </div>

              <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3 sm:[&>:last-child:nth-child(odd)]:col-span-2 xl:[&>:last-child:nth-child(odd)]:col-span-1">
                {sourceValue ? (
                  <InfoTile icon={<Globe size={14} />} label={sourceLabel}>
                    {sourceHref ? (
                      <Link href={sourceHref} className="transition-colors hover:text-blue-300">
                        {sourceValue}
                      </Link>
                    ) : (
                      sourceValue
                    )}
                  </InfoTile>
                ) : null}
                {coreValue ? (
                  <InfoTile icon={<Cpu size={14} />} label={coreLabel}>
                    {coreValue}
                  </InfoTile>
                ) : null}
                {typeValueFormatted ? (
                  <InfoTile icon={<Database size={14} />} label={typeLabel} preserveLines>
                    {typeValueFormatted}
                  </InfoTile>
                ) : null}
              </div>

              {objective.lines.length > 0 && (
                <div>
                  <span className="home-meta mb-4 block text-gray-500">{objectiveLabel}</span>
                  <ul className="home-subnote grid grid-cols-1 gap-x-10 gap-y-2.5 leading-relaxed text-gray-300 sm:grid-cols-2">
                    {objective.lines.map((line, i) => (
                      <li key={`obj-${i}`} className="flex min-w-0 gap-3">
                        <span
                          aria-hidden="true"
                          className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-blue-300/70"
                        />
                        <span className="min-w-0 break-words">{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="relative flex min-w-0 flex-col justify-between overflow-hidden border-t border-white/10 bg-[#0a0f1a]/70 p-6 sm:p-8 md:p-10 lg:col-span-5 lg:border-t-0 lg:border-l xl:col-span-4">
              <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />

              <div className="mb-8 sm:mb-10">
                <div className="mb-6 flex items-center justify-between border-b border-white/10 pb-4">
                  <span className="home-meta flex items-center gap-2 text-blue-300">
                    <CreditCard size={14} /> {budgetLabel}
                  </span>
                  <span className="home-meta text-gray-500">{budgetNote}</span>
                </div>

                <div
                  className={[
                    budgetCompact
                      ? 'text-[clamp(1.5rem,2.7vw,2.65rem)] tracking-[-0.06em]'
                      : 'text-[clamp(1.7rem,3vw,2.9rem)] tracking-tighter',
                    'max-w-full bg-gradient-to-br from-white to-cyan-800 bg-clip-text leading-none font-bold whitespace-nowrap text-transparent text-white tabular-nums',
                  ].join(' ')}
                >
                  {budgetValue}
                </div>
              </div>

              {features.length > 0 && (
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {features.map((f, i) => {
                    const Icon = iconForFeature(f)
                    return (
                      <m.div
                        key={`${f.label}-${i}`}
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.04 }}
                        className="flex min-h-[44px] cursor-default items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 transition-colors hover:bg-white/10 sm:min-h-[52px] sm:p-4 lg:p-5"
                      >
                        <Icon size={18} className="text-blue-300" />
                        <span className="home-caption leading-snug tracking-tight text-gray-200 uppercase">
                          {f.label}
                        </span>
                      </m.div>
                    )
                  })}
                </div>
              )}

              <div className="home-meta mt-8 flex items-center justify-between gap-3 border-t border-white/10 pt-6 text-gray-500">
                <span className="truncate">{footerLeft}</span>
                <span className="shrink-0">{footerRight}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
